/**
 * BundleBuilder — custom element managing the pick-your-bundle UI.
 *
 * Slot state shape:
 *   { handle, colour, swatchHex, swatchImg, variantId, size, optional }
 *
 */

class BundleBuilder extends HTMLElement {

  connectedCallback() {
    this._readConfig();
    this._initState();
    this._bindEvents();
    this._updateButton();
    this._initGallery();
  }

  _readConfig() {
    this.orMode       = this.dataset.orMode === 'true';
    this.slotCount    = parseInt(this.dataset.slotCount, 10) || 1;
    this.btnLabel     = this.dataset.btnLabel     || 'Add bundle to cart';
    this.pendingLabel = this.dataset.pendingLabel || 'Choose your bundle';
    this.btnEl        = this.querySelector('.bundle-add-to-cart');
    this.btnTextEl    = this.querySelector('.bundle-add-to-cart__text');
    this.errorEl      = this.querySelector('.bundle-builder__error');
    this._initialHandle = this.dataset.initialHandle || null;
    this._pageHandle    = this.dataset.pageHandle    || null;
  }

  _initState() {
    /** @type {Record<number, {handle:string|null,colour:string|null,swatchHex:string,swatchImg:string,variantId:string|null,size:string|null,optional:boolean}>} */
    this.state = {};

    this.querySelectorAll('.js-bundle-slot-init').forEach(el => {
      const d = JSON.parse(el.textContent);
      this.state[d.slot] = {
        handle:         d.handle   || null,
        handleB:        d.handleB  || null,
        colour:         d.colour   || null,
        colourB:        d.colourB  || null,
        swatchHex:      '',
        swatchImg:      '',
        featuredImg:    '',
        variantId:      null,
        size:           null,
        optional:       !!d.optional,
        activeSubSlot:  'a',
      };
    });

    this.querySelectorAll('.bundle-tab').forEach(tab => {
      const slot   = this._slotOf(tab);
      const active = tab.querySelector('.bundle-swatch--active');
      if (active && this.state[slot]) {
        this.state[slot].swatchHex   = active.dataset.swatchHex   || '';
        this.state[slot].swatchImg   = active.dataset.swatchImg   || '';
        this.state[slot].featuredImg = active.dataset.featuredImg || '';
      }
    });
  }

  _bindEvents() {
    this.addEventListener('click', e => {
      const swatch    = e.target.closest('.bundle-swatch');
      const sizeBtn   = e.target.closest('.bundle-size-btn');
      const clear     = e.target.closest('.bundle-tab__clear');
      const addBtn    = e.target.closest('.bundle-add-to-cart');
      const subToggle = e.target.closest('.bundle-subslot-btn');

      if (subToggle)      { e.preventDefault(); this._onSubSlotToggle(subToggle); }
      else if (swatch)    { e.preventDefault(); this._onSwatchClick(swatch); }
      else if (sizeBtn && !sizeBtn.disabled) { e.preventDefault(); this._onSizeClick(sizeBtn); }
      else if (clear)     { e.preventDefault(); e.stopPropagation(); this._onClear(clear); }
      else if (addBtn && !this.btnEl.disabled) { e.preventDefault(); this._onAddToCart(); }
    });
    
  }

  _onSubSlotToggle(btn) {
    const tab     = btn.closest('.bundle-tab');
    const slot    = this._slotOf(tab);
    const subslot = btn.dataset.subslot;
    const s       = this.state[slot];

    if (s.activeSubSlot === subslot) return;

    // Update toggle button states
    tab.querySelectorAll('.bundle-subslot-btn').forEach(b => {
      b.classList.toggle('bundle-subslot-btn--active', b.dataset.subslot === subslot);
    });

    // Show/hide subslot content panels
    tab.querySelectorAll('.bundle-subslot').forEach(el => {
      el.classList.toggle('bundle-subslot--hidden', el.dataset.subslot !== subslot);
    });

    s.activeSubSlot = subslot;

    // Restore defaults for the newly visible subslot from the active swatch
    const activePanel  = tab.querySelector(`.bundle-subslot[data-subslot="${subslot}"]`);
    const activeSwatch = activePanel?.querySelector('.bundle-swatch--active');

    // NEW: Update dynamic colour label when toggling sub-slots
    const colorLabelEl = activePanel?.querySelector('.js-swatch-color-label');
    if (colorLabelEl && activeSwatch) {
      colorLabelEl.textContent = activeSwatch.dataset.colour;
    }

    // Clear the slot selection — user must pick a size in the new subslot
    Object.assign(s, {
      handle:      activeSwatch?.dataset.handle      || (subslot === 'b' ? s.handleB : s.handle),
      colour:      activeSwatch?.dataset.colour      || (subslot === 'b' ? s.colourB : null),
      swatchHex:   activeSwatch?.dataset.swatchHex   || '',
      swatchImg:   activeSwatch?.dataset.swatchImg   || '',
      featuredImg: activeSwatch?.dataset.featuredImg || '',
      variantId:   null,
      size:        null,
    });

    // If the B subslot has no sizes yet, fetch them now
    if (subslot === 'b') {
      const sizesContainer = activePanel?.querySelector('.bundle-sizes');
      if (sizesContainer && sizesContainer.children.length === 0) {
        this._fetchSizes(s.handle, slot);
      }
    }

    this._renderSummary(slot);
    this._updateThumbnailOverlay();
    this._updateButton();
  }

 _onSwatchClick(swatch) {
    const tab    = swatch.closest('.bundle-tab');
    const slot   = this._slotOf(tab);
    const handle = swatch.dataset.handle;

    if (this.state[slot].handle === handle && this.state[slot].colour) return;

    tab.querySelectorAll('.bundle-swatch').forEach(s => {
      const active = s === swatch;
      s.classList.toggle('bundle-swatch--active', active);
      s.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    // NEW: Update dynamic colour label text above swatches
    const activePanel = swatch.closest('.bundle-subslot') || swatch.closest('.bundle-tab__content');
    const colorLabelEl = activePanel.querySelector('.js-swatch-color-label');
    if (colorLabelEl) {
      colorLabelEl.textContent = swatch.dataset.colour;
    }

    Object.assign(this.state[slot], {
      handle,
      colour:      swatch.dataset.colour      || null,
      swatchHex:   swatch.dataset.swatchHex   || '',
      swatchImg:   swatch.dataset.swatchImg   || '',
      featuredImg: swatch.dataset.featuredImg || '',
      variantId:   null,
      size:        null,
    });

    if (slot === 1) this._refreshGallery(handle);
    if (slot !== 1) this._updateThumbnailOverlay();

    this._renderSummary(slot);
    this._updateButton();
    this._fetchSizes(handle, slot);
  }

  async _fetchSizes(handle, slot) {
    const tab         = this._tabEl(slot);
    const activePanel = tab.querySelector(`.bundle-subslot[data-subslot="${this.state[slot]?.activeSubSlot || 'a'}"]`) || tab;
    const container   = activePanel.querySelector('.bundle-sizes');

    container.dataset.loading = '';
    container.innerHTML = '<span class="bundle-sizes__loading">Loading sizes…</span>';

    try {
      const resp = await fetch(`/products/${handle}.js`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();

      const sizeIdx = data.options.findIndex(o => {
        const name = typeof o === 'string' ? o : (o.name || '');
        return name.toLowerCase().includes('size');
      });
      const optKey = `option${sizeIdx >= 0 ? sizeIdx + 1 : 1}`;

      const sizeMap = new Map();
      data.variants.forEach(v => {
        const label = v[optKey];
        if (!label) return;
        if (!sizeMap.has(label)) {
          sizeMap.set(label, { id: v.id, available: v.available });
        } else if (v.available) {
          sizeMap.get(label).available = true;
        }
      });

      container.innerHTML = [...sizeMap.entries()].map(([label, { id, available }]) =>
        `<button
          type="button"
          class="bundle-size-btn${available ? '' : ' bundle-size-btn--sold-out'}"
          data-size="${label}"
          data-variant-id="${id}"
          ${available ? '' : 'disabled'}
        >${label}</button>`
      ).join('');

    } catch (err) {
      container.innerHTML = '<span class="bundle-sizes__error">Could not load sizes — please refresh.</span>';
      console.error('[BundleBuilder] fetchSizes:', err);
    }

    delete container.dataset.loading;
  }

  _onSizeClick(btn) {
    const tab  = btn.closest('.bundle-tab');
    const slot = this._slotOf(tab);

    tab.querySelectorAll('.bundle-size-btn').forEach(b => b.classList.remove('bundle-size-btn--active'));
    btn.classList.add('bundle-size-btn--active');

    this.state[slot].variantId = btn.dataset.variantId;
    this.state[slot].size      = btn.dataset.size;

    this._renderSummary(slot);

    if (this.orMode) {
      const sibling = slot === 2 ? 3 : slot === 3 ? 2 : null;
      if (sibling) this._setSlotDisabled(sibling, true);
    }

    if (slot !== 1) this._updateThumbnailOverlay();
    this._updateButton();
    this._advance(slot);
  }

  _onClear(clearBtn) {
    const tab  = clearBtn.closest('.bundle-tab');
    const slot = this._slotOf(tab);

    this.state[slot].variantId = null;
    this.state[slot].size      = null;

    tab.querySelectorAll('.bundle-size-btn').forEach(b => b.classList.remove('bundle-size-btn--active'));
    this._renderSummary(slot);

    if (this.orMode) {
      const sibling = slot === 2 ? 3 : slot === 3 ? 2 : null;
      if (sibling) this._setSlotDisabled(sibling, false);
    }

    if (slot !== 1) this._updateThumbnailOverlay();

    this._updateButton();
  }

  _renderSummary(slot) {
    const s      = this.state[slot];
    const tab    = this._tabEl(slot);
    if (!tab) return;

    const infoEl    = tab.querySelector('.bundle-tab__info');
    const previewEl = tab.querySelector('.bundle-tab__swatch-preview');
    const textEl    = tab.querySelector('.bundle-tab__selection-text');

    if (s.size) {
      previewEl.style.cssText = [
        `--swatch-background:${s.swatchHex || 'transparent'}`,
        s.swatchImg ? `--swatch-image:url('${s.swatchImg}')` : '',
      ].filter(Boolean).join(';');

      textEl.textContent = `${s.colour} • ${s.size}`;
      infoEl.classList.add('bundle-tab__info--visible');
    } else {
      infoEl.classList.remove('bundle-tab__info--visible');
    }
  }

  _updateButton() {
    const s1 = !!this.state[1]?.variantId;
    const s2 = !!this.state[2]?.variantId;
    const s3 = !!this.state[3]?.variantId;

    let ready;
    if (this.slotCount === 1) {
      ready = s1;
    } else if (this.slotCount === 2) {
      ready = s1 && s2;
    } else {
      // 3 slots
      ready = this.orMode ? s1 && (s2 || s3) : s1 && s2 && s3;
    }

    this.btnEl.disabled        = !ready;
    this.btnTextEl.textContent = ready ? this.btnLabel : this.pendingLabel;
  }

  async _onAddToCart() {
    const items = Object.values(this.state)
      .filter(s => s.variantId)
      .map(s => ({ id: parseInt(s.variantId, 10), quantity: 1 }));

    if (!items.length) return;

    this.btnEl.disabled = true;
    this.btnEl.classList.add('btn--loading');
    this.errorEl.hidden = true;

    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle },
      })
    );

    const body = JSON.stringify({
      items,
      sections: sectionsToBundle,
      section_url: window.location.pathname,
    });

    try {
      const addResp = await fetch(FoxTheme.routes.cart_add_url, {
        ...FoxTheme.utils.fetchConfig('javascript'),
        body,
      });

      const parsedAdd = await addResp.json();

      if (parsedAdd.status) {
        throw new Error(parsedAdd.description || 'Could not add items to cart.');
      }

      const cartResp = await fetch(FoxTheme.routes.cart_url, {
        ...FoxTheme.utils.fetchConfig(),
      });
      const cartJson = await cartResp.json();
      cartJson.sections = parsedAdd.sections;

      FoxTheme.pubsub.publish(
        FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
        { cart: cartJson }
      );

      document.dispatchEvent(
        new CustomEvent('product-ajax:added', {
          detail: { product: parsedAdd },
        })
      );

      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        cartDrawer.show(this.btnEl);
      }

    } catch (err) {
      this.errorEl.textContent = err.message;
      this.errorEl.hidden = false;
      this.btnEl.disabled = false;
    } finally {
      this.btnEl.classList.remove('btn--loading');
    }
  }

  _advance(doneSlot) {
    for (let i = doneSlot + 1; i <= 3; i++) {
      const next = this._tabEl(i);
      if (next && !next.classList.contains('bundle-tab--disabled')) {
        next.open = true;
        next.setAttribute('aria-expanded', 'true');
        break;
      }
    }
  }

  _setSlotDisabled(slot, disabled) {
    const tab = this._tabEl(slot);
    if (!tab) return;
    tab.classList.toggle('bundle-tab--disabled', disabled);
    tab.inert = disabled;
    if (disabled && tab.open) { tab.open = false; tab.setAttribute('aria-expanded', 'false'); }
  }

  _initGallery() {
    const productInfo = document.querySelector('product-info[data-section]');
    this._sectionId     = productInfo?.dataset.section || null;
    this._galleryHandle = null;
    this._galleryFetching = false;
    if (!this._sectionId) return;

    this._ensureThumbnailOverlay();

    if (this._initialHandle && this._initialHandle !== this._pageHandle) {
      this._refreshGallery(this._initialHandle);
    }
  }

  async _refreshGallery(handle) {
    if (!this._sectionId) return;
    if (this._galleryFetching) return;
    if (handle === this._galleryHandle) return;

    this._galleryFetching = true;

    try {
      const resp = await fetch(`/products/${handle}?section_id=${this._sectionId}`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

      const doc = new DOMParser().parseFromString(await resp.text(), 'text/html');

      window.history.replaceState({}, '', `/products/${handle}`);

      const wrapper = document.querySelector('.product__media-wrapper');
      const existingGallery = wrapper?.querySelector('media-gallery');
      const newGallery = doc.querySelector('media-gallery');

      if (existingGallery && newGallery) {
        existingGallery.style.transition = 'opacity 0.15s ease';
        existingGallery.style.opacity = '0';

        newGallery.style.opacity = '0';
        newGallery.style.transform = 'translateY(8px)';
        newGallery.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
        existingGallery.replaceWith(newGallery);

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            newGallery.style.opacity = '1';
            newGallery.style.transform = 'translateY(0)';
          });
        });
      }

      const currentBlocks = Array.from(document.querySelectorAll('.product__block:not(.product__block--bundle_builder)'));
      const newBlocks = Array.from(doc.querySelectorAll('.product__block:not(.product__block--bundle_builder)'));
      
      currentBlocks.forEach((currentBlock) => {
        if (currentBlock.tagName.toLowerCase() === 'media-gallery' || currentBlock.querySelector('media-gallery')) {
          return; 
        }

        let newBlock = null;

        const typeClass = Array.from(currentBlock.classList).find(
          className => className.startsWith('product__block--')
        );

        if (currentBlock.id && doc.getElementById(currentBlock.id)) {
          newBlock = doc.getElementById(currentBlock.id);
        } else if (typeClass) {
          const currentTypeBlocks = currentBlocks.filter(b => b.classList.contains(typeClass));
          const newTypeBlocks = newBlocks.filter(b => b.classList.contains(typeClass));
          
          const blockIndex = currentTypeBlocks.indexOf(currentBlock);
          
          if (blockIndex > -1 && newTypeBlocks[blockIndex]) {
            newBlock = newTypeBlocks[blockIndex];
          }
        }

        if (newBlock) {
          if (!currentBlock.isEqualNode(newBlock)) {
            currentBlock.replaceWith(newBlock);
          }
        } else {
          currentBlock.remove();
        }
      });

      this._galleryHandle = handle;

      this._ensureThumbnailOverlay();
      this._updateThumbnailOverlay();

    } catch (err) {
      console.error('[BundleBuilder] refreshGallery:', err);
    } finally {
      this._galleryFetching = false;
    }
  }

  _ensureThumbnailOverlay() {
    const gallery = document.querySelector('media-gallery');
    if (!gallery) return;

    document.querySelectorAll('.bundle-gallery-thumb-wrap').forEach(old => {
      if (!gallery.contains(old)) old.remove();
    });

    let wrap = gallery.querySelector('.bundle-gallery-thumb-wrap');
    if (!wrap) {
      wrap = document.createElement('div');
      wrap.className = 'bundle-gallery-thumb-wrap';
      wrap.innerHTML = `
        <div class="bundle-gallery-thumb" data-thumb-slot="2">
          <img class="bundle-gallery-thumb__img" src="" alt="" loading="lazy">
        </div>
        <div class="bundle-gallery-thumb" data-thumb-slot="3">
          <img class="bundle-gallery-thumb__img" src="" alt="" loading="lazy">
        </div>
      `;
      gallery.appendChild(wrap);
    }
    this._thumbnailWrapEl = wrap;
  }

  _updateThumbnailOverlay() {
    const wrap = this._thumbnailWrapEl;
    if (!wrap) return;

    let anyVisible = false;

    [2, 3].forEach(n => {
      const s   = this.state[n];
      const el  = wrap.querySelector(`.bundle-gallery-thumb[data-thumb-slot="${n}"]`);
      if (!el) return;

      if (s?.size && s?.featuredImg) {
        const img = el.querySelector('.bundle-gallery-thumb__img');
        const src = s.featuredImg;
        if (img.getAttribute('src') !== src) {
          img.style.opacity = '0';
          img.src           = src;
          img.onload        = () => { img.style.opacity = ''; };
        }
        el.classList.add('bundle-gallery-thumb--visible');
        anyVisible = true;
      } else {
        el.classList.remove('bundle-gallery-thumb--visible');
      }
    });

    wrap.classList.toggle('bundle-gallery-thumb-wrap--visible', anyVisible);
  }

  _slotOf(el) { return parseInt(el.closest('[data-slot]')?.dataset.slot ?? '0', 10); }
  _tabEl(slot) { return this.querySelector(`.bundle-tab[data-slot="${slot}"]`); }
}

if (!customElements.get('bundle-builder')) {
  customElements.define('bundle-builder', BundleBuilder);
}
